import type { ArtifactTree } from '../types/artifacts';

type TreeWithMeta = ArtifactTree & {
  tree_metadata?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  company_id?: string;
};

function readTreeMeta(tree: TreeWithMeta): Record<string, unknown> | undefined {
  const candidates = [tree.tree_metadata, tree.metadata];
  for (const metadata of candidates) {
    if (metadata && typeof metadata === 'object') {
      return metadata as Record<string, unknown>;
    }
  }
  return undefined;
}

function readMetadataSlug(tree: TreeWithMeta): string | undefined {
  const meta = readTreeMeta(tree);
  if (!meta) return undefined;
  for (const key of ['kryspace_slug', 'engineering_workspace_slug', 'kryspace_workspace_slug']) {
    const value = meta[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/** Match a DB artifact tree row to a kryspace workspace model slug. */
export function resolveTreeForModelSlug(
  trees: ArtifactTree[],
  modelSlug: string,
  opts?: { companyId?: string }
): ArtifactTree | undefined {
  const slug = modelSlug.trim().toLowerCase();
  if (!slug) return undefined;

  const exact = trees.find((tree) => {
    const record = tree as TreeWithMeta;
    const metaSlug = readMetadataSlug(record);
    if (metaSlug?.toLowerCase() === slug) return true;
    return tree.name?.trim().toLowerCase().replace(/\s+/g, '_') === slug;
  });
  if (exact) return exact;

  const companyId = opts?.companyId?.trim().toLowerCase();
  if (companyId) {
    const forCompany = trees.filter((tree) => {
      const record = tree as TreeWithMeta;
      const cid = record.company_id ? String(record.company_id).toLowerCase() : '';
      if (cid && cid === companyId) return true;
      const meta = readTreeMeta(record);
      const metaCid = meta?.company_id;
      return typeof metaCid === 'string' && metaCid.trim().toLowerCase() === companyId;
    });
    if (forCompany.length === 1) {
      return forCompany[0];
    }
  }

  if (trees.length === 1) {
    return trees[0];
  }

  return undefined;
}
